import FilterHelper from '../../helpers/filterHelper';
import { formatQueryStringURL } from '../../helpers/formatQueryStringHelper';
import {
	axiosDelete,
	axiosGet,
	axiosPatch,
	axiosPost,
	axiosPut,
} from '../../services/axiosService';

const investorURL = '/Lenders';
const baseMarketplaceURI = '/MarketPlace';

export class InvestorService {
	getAllInvestor = (
		status,
		filter = null,
		currentPage = 1,
		perPage = 10,
		extraFilter
	) => {
		let sortBy = FilterHelper.mapFilterStringToForm(filter, 'sortBy') || '1';
		let sortDescending = FilterHelper.mapFilterStringToForm(filter, 'sortDescending');
		if (sortDescending === undefined || sortDescending === '' || sortDescending === null) {
			sortDescending = 'true';
		}

		let filterWithoutSort = filter;
		if (filter) {
			const filterArray = filter.split(',').filter(item => {
				const key = item.split('=')[0];
				return key !== 'sortBy' && key !== 'sortDescending';
			});
			filterWithoutSort = filterArray.length > 0 ? filterArray.join(',') : null;
		}

		let params = `${status ? `investorStatuses=${status}` : ''}${filterWithoutSort ? `${FilterHelper.mapToParams(filterWithoutSort)}` : ''
			}&Paging.CurrentPage=${currentPage}&Paging.PerPage=${perPage}&SortBy=${sortBy}&SortDescending=${sortDescending}${extraFilter ? '&' + extraFilter : ''
			}`;
		params = formatQueryStringURL('?' + params);
		return axiosGet(null, investorURL + params);
	};

	getInvestorDetail = (id) => {
		return axiosGet(null, investorURL, id);
	};

	resendVerificationEmail(id) {
		return axiosPost(null, `${investorURL}/${id}/verify/email`);
	}

	generateVirtualIban(id) {
		return axiosPost(null, `/Users/${id}/virtual-iban`);
	}

	investorBecomeProCount = () => {
		return axiosGet(null, '/LenderSummary/GetRequestedBecameProCount');
	};

	updateLenderDetailsById(formValue, id) {
		return axiosPut(formValue, `${investorURL}/${id}`);
	}

	getCorporateInformation(id) {
		return axiosGet(null, `${investorURL}/${id}/CorporateInformation`);
	}

	updateCorporateInformation(formValue, id) {
		return axiosPut(formValue, `${investorURL}/${id}/CorporateInformation`);
	}

	exportInvestors = (status, filter = null) => {
		return axiosGet(
			null,
			`${investorURL}/export${status ? `?investorStatuses=${status}` : ''}${this.checkFilter(filter) ? `&${FilterHelper.mapToParams(filter)}` : ''
			}`,
			null,
			'blob'
		);
	};

	checkFilter(filter) {
		if (filter) {
			if (typeof filter === 'object') {
				return Object.keys(filter).length ? filter : null;
			}
			return filter;
		}
		return null;
	}

	onBoarding(id) {
		return axiosPost(null, `${investorURL}/${id}/onboarding`);
	}

	getLenderLoans(id) {
		return axiosGet(null, `${investorURL}/${id}/loans`);
	}

	getLoanParts(id) {
		return axiosGet(null, `LenderSummary/${id}/Investments`);
	}

	getTransactionStatementsByLenderId(id, year) {
		return axiosGet({ year: year }, `LenderSummary/${id}/Statements`);
	}

	exportTransactionStatements(id, year) {
		return axiosGet(
			{ year: year },
			`LenderSummary/${id}/Statements/Export?year=${year}`,
			null,
			'blob'
		);
	}

	investMarketplaceOnbehalfOfLender(id, lenderId, form) {
		let body = {
			lenderId,
			amount: form.amount
		};
		if (form?.code) {
			body["promoCode"] = form.code;
		}
		return axiosPost(
			body,
			baseMarketplaceURI + `/invest/${id}/on-behalf-of-lender`,
			{ hide_success_toaster: true }
		);
	}

	getMarketplace(lenderId) {
		return lenderId
			? axiosGet(null, `${baseMarketplaceURI}/for-lender/${lenderId}`)
			: axiosGet(null, baseMarketplaceURI);
	}

	getLenderSummaryShortById(id) {
		return axiosGet(null, `/LenderSummary/${id}/Short`);
	}

	// The financial summary fields (totalDeposit, portfolioTotal, investedAmount,
	// ordersAmount, availableCash) shown on the Approved Investors list are only returned by
	// the list endpoint (neither /Lenders/{id} nor /LenderSummary/{id}/Short carry all five —
	// verified against the dev API). Filtering the list by email is the only way to fetch
	// them for a single investor.
	getInvestorFinancialSummaryByEmail(email) {
		return axiosGet(
			{ email, 'Paging.CurrentPage': 1, 'Paging.PerPage': 1 },
			investorURL
		);
	}

	addFollowUpDate(form, id) {
		return axiosPatch(`${investorURL}/${id}/UpdateFollowUpDates`, form);
	}

	saveInvestorNotes(comment, id) {
		return axiosPost(comment, `${investorURL}/${id}/notes`);
	}

	deleteInvestorNote(id, commentID) {
		return axiosDelete(`${investorURL}/${id}/notes/${commentID}`);
	}

	getInvestorNotes(id) {
		return axiosGet(null, `${investorURL}/${id}/notes`);
	}

	updateFollowUpInformation(id, model) {
		return axiosPatch(investorURL + `/${id}/FollowUpInformation`, model);
	}

	getInvestorCountByStatus(status = '', followUpStatus) {
		const query =
			followUpStatus !== undefined && followUpStatus !== null
				? `?investorFollowUpStatus=${followUpStatus}`
				: '';
		return axiosGet(null, `${investorURL}/CountByStatus/${status}${query}`);
	}

	getPassportExpiryCount() {
		return axiosGet(null, `${investorURL}/CountByPassportExpired`);
	}

	getAnnualReviewCount() {
		return axiosGet(null, `${investorURL}/CountByAnnualReview`);
	}

	getPendingIbanCount() {
		return axiosGet(null, `${investorURL}/CountByPendingIban`);
	}

	getInvestorComments(id) {
		return axiosGet(null, `${investorURL}/${id}/comments`);
	}

	updateComments(id, model) {
		return axiosPut(model, investorURL + `/${id}/comments`);
	}

	getIDWiseReport(userId) {
		return axiosPost(null, `/AML/GetAmlReport/${userId}`, { hide_success_toaster: true });
	}

	refreshIDWiseReport(userId) {
		return axiosPost(null, `/AML/RefreshAmlReport/${userId}`, { hide_success_toaster: true });
	}

	checkPromoCode(form) {
		return axiosPost(form, 'PromoCodes/check-eligibility');
	}

	getIntegration(id) {
		return axiosGet(null, `${investorURL}/${id}/integration`);
	}

	updateIntegration(id, data) {
		return axiosPatch(investorURL + `/${id}/integration`, data);
	}

	getWatheqInfo(id) {
		return axiosGet(null, investorURL + `/${id}/WathQFullInfo`);
	}

	getRealBeneficiary(id) {
		return axiosGet(null, investorURL + `/${id}/WathQRealBeneficiary`);
	}

	getBankDetailsById(id) {
		return axiosGet(null, `/investors/${id}/bank-details`);
	}

	updateBankDetailsById(id, bankDetails) {
		return axiosPatch(`/investors/${id}/bank-details`, bankDetails);
	}

	updateIbanById(id, ibanData) {
		return axiosPatch(`/investors/${id}/iban`, ibanData);
	}

	approveIbanById(id) {
		return axiosPatch(`/investors/${id}/iban/approve`);
	}

	rejectIbanById(id) {
		return axiosPatch(`/investors/${id}/iban/reject`);
	}
}
